/**
 * Decodes the scrcpy backend's H.264 stream with WebCodecs and paints it to a canvas.
 *
 * In scrcpy mode the phone has no WebRTC video track. Encoded H.264 arrives on a data
 * channel labelled `video`: first a JSON header, then binary messages. Each access unit is
 * fragmented across messages so no single send exceeds the channel's max message size (an
 * oversized send resets the whole channel). Every message carries a 2-byte header: byte 0
 * is the type (0 = delta, 1 = key, with SPS/PPS prepended), byte 1 is 1 on the final
 * fragment of the unit. The channel is ordered and reliable, so fragments are concatenated
 * in arrival order. The keyframe's SPS gives the profile and level to configure the decoder.
 *
 * WebCodecs is Chrome/Edge; Safari support is partial. The viewer already targets
 * Chromium, and [isSupported] lets the caller show an honest message elsewhere.
 */

export interface StreamHeader {
  codec: string;
  width: number;
  height: number;
}

export function isSupported(): boolean {
  return typeof globalThis.VideoDecoder !== 'undefined';
}

export class ScreenDecoder {
  private decoder: VideoDecoder | null = null;
  private configured = false;
  private timestamp = 0;
  private readonly ctx: CanvasRenderingContext2D;

  /** Fragments of the access unit currently being reassembled. */
  private pendingChunks: Uint8Array[] = [];
  private pendingType = 0;

  /** Source frame size, for pointer coordinate mapping. Zero until the first frame. */
  frameWidth = 0;
  frameHeight = 0;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly onError: (message: string) => void,
  ) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2d canvas context unavailable');
    this.ctx = ctx;
  }

  /** The JSON header message. Advisory; sizes are also taken from decoded frames. */
  handleHeader(header: StreamHeader): void {
    this.frameWidth ||= header.width;
    this.frameHeight ||= header.height;
  }

  handleChunk(buffer: ArrayBuffer): void {
    const bytes = new Uint8Array(buffer);
    if (bytes.length < 2) return;
    this.pendingType = bytes[0]!; // consistent across a unit's fragments
    this.pendingChunks.push(bytes.subarray(2));
    if (bytes[1] !== 1) return; // not the final fragment yet

    const unit = concat(this.pendingChunks);
    this.pendingChunks = [];
    this.decodeUnit(this.pendingType === 1, unit);
  }

  private decodeUnit(isKey: boolean, unit: Uint8Array): void {
    if (!this.configured) {
      // Decoding has to start on a key frame; ignore anything before the first one.
      if (!isKey) return;
      if (!this.configure(unit)) return;
    }

    const chunk = new EncodedVideoChunk({
      type: isKey ? 'key' : 'delta',
      timestamp: this.timestamp,
      data: unit,
    });
    this.timestamp += 33_333; // ~30fps in microseconds; display timing only
    try {
      this.decoder?.decode(chunk);
    } catch {
      this.recover();
    }
  }

  /**
   * Tears the decoder down so the next key frame reconfigures and resumes. A dropped or
   * bad frame corrupts H.264 decoding until then; without this the picture froze until a
   * full reconnect. The phone sends key frames about once a second, so recovery is quick.
   */
  private recover(): void {
    try {
      this.decoder?.close();
    } catch {
      /* already closed */
    }
    this.decoder = null;
    this.configured = false;
  }

  private configure(keyFrame: Uint8Array): boolean {
    const codec = codecStringFromAnnexB(keyFrame) ?? 'avc1.42E01E';
    try {
      this.decoder = new VideoDecoder({
        output: (frame) => this.draw(frame),
        // A runtime decode error is recoverable: reset and pick up the next key frame,
        // rather than surfacing a fatal error and freezing.
        error: () => this.recover(),
      });
      // No `description`: the stream is Annex-B, so parameter sets are in-band.
      this.decoder.configure({ codec, optimizeForLatency: true } as VideoDecoderConfig);
      this.configured = true;
      return true;
    } catch (error) {
      this.onError(`decoder configure failed (${codec}): ${(error as Error).message}`);
      return false;
    }
  }

  private draw(frame: VideoFrame): void {
    this.frameWidth = frame.displayWidth;
    this.frameHeight = frame.displayHeight;
    if (this.canvas.width !== frame.displayWidth || this.canvas.height !== frame.displayHeight) {
      this.canvas.width = frame.displayWidth;
      this.canvas.height = frame.displayHeight;
    }
    this.ctx.drawImage(frame, 0, 0);
    frame.close();
  }

  close(): void {
    try {
      this.decoder?.close();
    } catch {
      /* already closed */
    }
    this.decoder = null;
    this.configured = false;
    this.pendingChunks = [];
  }
}

/**
 * Builds a WebCodecs codec string (`avc1.PPCCLL`) from the SPS in an Annex-B key frame.
 * The SPS NAL (type 7) begins, after its one-byte header, with profile_idc,
 * constraint flags, and level_idc, which are exactly the three bytes WebCodecs wants.
 * Returns null if no SPS is found, so the caller can fall back to a generic string.
 */
function codecStringFromAnnexB(data: Uint8Array): string | null {
  for (let i = 0; i + 4 < data.length; i += 1) {
    const startCode3 = data[i] === 0 && data[i + 1] === 0 && data[i + 2] === 1;
    const startCode4 = data[i] === 0 && data[i + 1] === 0 && data[i + 2] === 0 && data[i + 3] === 1;
    if (!startCode3 && !startCode4) continue;

    const nalStart = i + (startCode4 ? 4 : 3);
    const header = data[nalStart];
    if (header === undefined) break;
    if ((header & 0x1f) === 7 && nalStart + 3 < data.length) {
      const profile = data[nalStart + 1]!;
      const constraints = data[nalStart + 2]!;
      const level = data[nalStart + 3]!;
      return `avc1.${hex(profile)}${hex(constraints)}${hex(level)}`;
    }
    i = nalStart - 1;
  }
  return null;
}

function hex(value: number): string {
  return value.toString(16).padStart(2, '0').toUpperCase();
}

/** Concatenates access-unit fragments into one contiguous buffer. */
function concat(chunks: Uint8Array[]): Uint8Array {
  let total = 0;
  for (const chunk of chunks) total += chunk.length;
  const out = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.length;
  }
  return out;
}
